import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Eye, X } from 'lucide-react';

interface SnapshotDatePickerProps {
  onViewSnapshot: (date: string) => void;
  onClearSnapshot: () => void;
  activeSnapshot: string | null;
  isLoading?: boolean;
}

const SnapshotDatePicker: React.FC<SnapshotDatePickerProps> = ({
  onViewSnapshot,
  onClearSnapshot,
  activeSnapshot,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string>('');

  const handleViewSnapshot = () => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      const now = new Date();
      now.setHours(23, 59, 59, 999);

      if (date > now) {
        alert(t('warehouse.snapshot.futureDate'));
        return;
      }

      onViewSnapshot(selectedDate);
    }
  };

  const handleClear = () => {
    setSelectedDate('');
    onClearSnapshot();
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 flex-1">
          <Calendar className="w-5 h-5 text-gray-500" />
          <label className="text-sm font-medium text-gray-700">
            {t('warehouse.snapshot.viewStockAt')}:
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading}
          />
        </div>
        <button
          onClick={handleViewSnapshot}
          disabled={!selectedDate || isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          {isLoading ? t('common.loading') : t('warehouse.snapshot.viewSnapshot')}
        </button>
        {activeSnapshot && (
          <button
            onClick={handleClear}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            {t('warehouse.snapshot.clearSnapshot')}
          </button>
        )}
      </div>

      {activeSnapshot && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>{t('warehouse.snapshot.showingStockAt', { date: new Date(activeSnapshot).toLocaleDateString() })}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default SnapshotDatePicker;
